// Live-measurement contract for Atomic Mode.
//
// The generated LAMMPS script defines a handful of equal-style variables with
// the names below. The vendored atomify bridge
// (vendor/atomify/src/components/EmbedStatusReporter.tsx) reads their scalar
// values from the LAMMPS store on every sync and posts them to the host as a
// `measurement` event. The host (AtomicView) turns the stream into the live
// neon readout — temperature, potential energy, diffusion coefficient — and the
// "reaction detected" flag shown in the Atomic Mode log.
//
// The variable NAMES are a stable contract duplicated in the bridge (which can't
// import host code across the GPL boundary), exactly like the postMessage event
// shapes in atomifyEmbed.ts.

/** Equal-style variable names the script exposes and the bridge reads. These
 * reference thermo keywords (temp/pe/ke), which are always current, so polling
 * them off-schedule (as atomify does each sync) is safe. */
export const MEASUREMENT_VARS = {
  temp: "mdTemp",
  pe: "mdPe",
  ke: "mdKe",
} as const;

/** Fix id holding the time-averaged MSD total. The bridge reads this fix's
 * stored *scalar* from the store — NOT via an equal-style variable. A variable
 * that references a compute/fix (`v = c_msd[4]` or `v = f_...`) breaks when
 * atomify evaluates it off the fix's output schedule: the first off-schedule
 * read throws "fix not computed at a compatible time", which leaves LAMMPS's
 * variable-eval flag stuck and turns every later read into a bogus "circular
 * dependency". A fix's stored scalar has no such evaluation state — it just
 * returns the last averaged value (0 before the first output). */
export const MEASUREMENT_MSD_FIX = "mdMsdAvg";

/**
 * LAMMPS lines that expose the live observables for the bridge to read each
 * sync. Temp/PE/KE are equal-style variables over thermo keywords (always
 * current). MSD is stashed in a `fix ave/time` scalar (`MEASUREMENT_MSD_FIX`)
 * that the bridge reads from the synced fix object — deliberately NOT via a
 * variable, which would break under atomify's off-schedule polling (see the
 * note on `MEASUREMENT_MSD_FIX`). When `hasMsd`, the caller must have already
 * defined `compute msd all msd`. Insert these right after the measurement
 * computes, before the `run`.
 */
export function measurementVariableLines(hasMsd = true): string[] {
  const L = [
    "# live measurements surfaced to the Atomic Mode log (read by the bridge)",
    `variable ${MEASUREMENT_VARS.temp} equal temp`,
    `variable ${MEASUREMENT_VARS.pe} equal pe`,
    `variable ${MEASUREMENT_VARS.ke} equal ke`,
  ];
  if (hasMsd) {
    // Time-average the MSD total into a fix scalar; the bridge reads the fix's
    // stored scalar (no variable → no off-schedule eval / circular-dependency).
    L.push(
      `fix ${MEASUREMENT_MSD_FIX} all ave/time 10 1 10 c_msd[4] mode scalar`,
    );
  }
  return L;
}

/** One live sample posted by the bridge. Energy in the run's units. */
export interface Measurement {
  step: number;
  temp: number | null;
  pe: number | null;
  ke: number | null;
  /** Mean-squared displacement total (Å²), or null if no MSD compute. */
  msd: number | null;
  numAtoms: number;
  numBonds: number;
}

/**
 * Picoseconds per timestep for the run, parsed from the script's `units` and
 * `timestep`. metal → ps already; real → fs (÷1000); lj/other → null (no
 * physical time, so we won't quote a diffusion coefficient in real units).
 */
export function picosecondsPerStep(inputScript: string): number | null {
  const units = /^\s*units\s+(\w+)/m.exec(inputScript)?.[1] ?? "metal";
  const dt = Number.parseFloat(
    /^\s*timestep\s+([0-9.eE+-]+)/m.exec(inputScript)?.[1] ?? "",
  );
  if (!Number.isFinite(dt)) return null;
  if (units === "metal") return dt; // metal timestep is in ps
  if (units === "real") return dt / 1000; // real timestep is in fs
  return null; // lj / si / other — no Å²/ps interpretation
}

/**
 * Self-diffusion coefficient from an MSD time series via the 3D Einstein
 * relation D = slope(MSD vs t) / 6. Uses a least-squares fit over the LATER
 * portion of the series (skips the first third as equilibration/ballistic
 * regime). Returns D in Å²/ps, or null until the fit is meaningful.
 *
 * `points` are { t: picoseconds, msd: Å² }. Needs the MSD to have grown — a
 * frozen solid has ~zero slope and correctly reports D ≈ 0.
 */
export function diffusionCoefficient(
  points: { t: number; msd: number }[],
): number | null {
  if (points.length < 6) return null;
  // Drop the initial transient (ballistic / equilibration).
  const start = Math.floor(points.length / 3);
  const fit = points.slice(start);
  if (fit.length < 4) return null;
  const n = fit.length;
  let sx = 0;
  let sy = 0;
  let sxx = 0;
  let sxy = 0;
  for (const p of fit) {
    sx += p.t;
    sy += p.msd;
    sxx += p.t * p.t;
    sxy += p.t * p.msd;
  }
  const denom = n * sxx - sx * sx;
  if (Math.abs(denom) < 1e-12) return null;
  const slope = (n * sxy - sx * sy) / denom; // Å²/ps
  const d = slope / 6;
  return Number.isFinite(d) ? Math.max(0, d) : null;
}
